/**
 * ThinkingIndicator - Collapsible display for AI streaming thoughts
 *
 * Features:
 * - Collapsible <details> element for showing/hiding thoughts
 * - Auto-scroll to bottom as new thoughts stream in
 * - Fixed max-height with overflow scrolling
 * - Subtle styling that doesn't distract from main content
 *
 * Use case:
 * - Displays AI's "thinking" process in real-time
 * - Helps users understand what the AI is considering
 * - Builds trust through transparency
 *
 * Example thoughts:
 * "Analyzing the user's request... Need to break this into steps..."
 * "Step 1: Fetch data from API... Step 2: Process results..."
 */
import { useEffect, useRef } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * ThinkingIndicatorProps - Props for the ThinkingIndicator component
 *
 * @property thoughts - The streaming text from the AI's thinking process
 * @property defaultOpen - Whether the details element starts open (default: false)
 */
interface ThinkingIndicatorProps {
  /** The AI's streaming thoughts text */
  thoughts: string;

  /** Whether to start with the indicator expanded */
  defaultOpen?: boolean;
}

// ============================================================================
// THINKING INDICATOR COMPONENT
// ============================================================================

/**
 * ThinkingIndicator - The collapsible thinking display
 *
 * Implementation notes:
 * - Uses native <details> element for built-in expand/collapse behavior
 * - Auto-scrolls to bottom as thoughts stream using useRef and useEffect
 * - <pre> tag preserves formatting and line breaks from AI
 * - max-height and overflow-auto provide scrolling for long thoughts
 *
 * Styling choices:
 * - Gray background (subtle, non-distracting)
 * - Smaller text size (secondary information)
 * - Rounded corners (consistent with platform design)
 */
export function ThinkingIndicator({ thoughts, defaultOpen = false }: ThinkingIndicatorProps) {
  // Reference to the <pre> element for auto-scrolling
  const preRef = useRef<HTMLPreElement>(null);

  // ---------------------------------------------------------------------------
  // AUTO-SCROLL EFFECT
  // ---------------------------------------------------------------------------

  /**
   * Auto-scroll to bottom when thoughts update
   *
   * This ensures the user always sees the latest thought without
   * manually scrolling. Important for streaming updates where new
   * text appears continuously.
   */
  useEffect(() => {
    if (preRef.current) {
      // Scroll to the bottom of the <pre> element
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [thoughts]); // Re-run when thoughts change

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <details
      className="group"
      open={defaultOpen}
    >
      {/* =================================================================
          SUMMARY - Clickable header that toggles expand/collapse
          Arrow indicator shows current state (▶ closed, ▼ open)
          ================================================================= */}
      <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1">
        {/* Arrow indicator (rotates when open) */}
        <svg
          className="w-3 h-3 transition-transform group-open:rotate-90"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>

        {/* Label text */}
        <span>Thinking...</span>

        {/* Subtle animation dots to indicate active streaming */}
        <span className="inline-flex gap-0.5">
          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
        </span>
      </summary>

      {/* =================================================================
          CONTENT - The actual thoughts text
          Wrapped in <pre> to preserve formatting and line breaks
          ================================================================= */}
      <div className="mt-2 rounded-md bg-gray-100 border border-gray-200 p-3">
        <pre
          ref={preRef}
          className="text-xs text-gray-600 whitespace-pre-wrap break-words overflow-auto font-mono"
          style={{
            maxHeight: '120px', // Limit height to prevent overwhelming the UI
          }}
        >
          {thoughts || 'Processing...'}
        </pre>
      </div>
    </details>
  );
}
